import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

/**
 * Interface language.
 *
 * Urdu is not a nice-to-have here. The activities are scripts a parent reads
 * aloud to a child — "what you might say" is meant to be said — and a parent
 * who thinks in Urdu should not have to translate the prompt in their head
 * before speaking it. So both the chrome and the authored content are
 * translatable, and they switch together.
 */
export const locales = [
  { code: "en", label: "English", native: "English", dir: "ltr" as const },
  { code: "ur", label: "Urdu", native: "اردو", dir: "rtl" as const },
];

export type LocaleCode = (typeof locales)[number]["code"];

export const DEFAULT_LOCALE: LocaleCode = "en";

export function localeDir(code: string) {
  return locales.find((locale) => locale.code === code)?.dir ?? "ltr";
}

const STORAGE_KEY = "alnoor-homeschool-locale";

type Dictionary = Record<string, string>;

const en: Dictionary = {
  "home.plannedMoments": "Planned moments",
  "home.learningTime": "Learning time",
  "home.learningAreas": "Learning areas",
  // Chrome
  "brand.sub": "HOMESCHOOL",
  "nav.familySection": "Family space",
  "nav.programme": "Programme",
  "nav.home": "Home",
  "nav.today": "Today",
  "nav.subscription": "Subscription",
  "nav.curriculum": "Curriculum",
  "nav.mastery": "Mastery map",
  "nav.portfolio": "Portfolio",
  "nav.reports": "Reports",
  "nav.activities": "Activity library",
  "nav.competencies": "Competencies",
  "nav.main": "Main navigation",
  "side.quote": "Small moments, lasting growth.",
  "side.signOut": "Sign out",
  "top.openNav": "Open navigation",
  "top.closeNav": "Close menu",
  "top.learningWith": "Learning with",
  "top.signedInAs": "Signed in as",
  "top.changeLearner": "Change learner",
  "top.addLearner": "Add a learner",
  "top.addLearnerHint": "Their own plan and portfolio",
  "top.recordEvidence": "Record evidence",
  "top.language": "Language",
  "top.urduBeta": "The Urdu translation is still being developed. If something reads wrongly, please send us a correction.",
  "top.urduBetaAction": "Send a suggestion",

  // Boot states
  "boot.loading": "Opening your family's learning…",
  "boot.loadingHint": "One moment.",
  "boot.unavailable": "Not connected yet",
  // Written for a family, not for whoever is running the dev server. The
  // old text told a parent to run npm, which is not a thing a parent can do.
  "boot.unavailableHint":
    "We could not connect to your learning space. Check your internet connection and try again.",
  "boot.signedOut": "Sign in to continue",
  "boot.signedOutHint":
    "Al Noor Homeschool uses your existing Al Noor Tuition account — there is no separate password to remember.",
  "boot.signIn": "Sign in",
  "boot.begin": "Let's begin",
  "boot.beginHint": "Add your first learner to build their first day.",
  // Signed-out landing
  "landing.eyebrow": "Playgroup to Class 5",
  "landing.title": "Teach your child at home, with a plan for every day.",
  "landing.lede":
    "A gentle, competency-based programme for families in Pakistan. Six short activities a day, planned for you and ready to use — and a record of what your child can actually do that builds itself as you go.",
  "landing.guideLink": "Read the homeschooling guide",
  "landing.accountNote": "Uses your existing Al Noor Tuition account. There is no separate password to remember.",
  "landing.dayEyebrow": "A day looks like this",
  "landing.dayTitle": "Just over an hour, in six moments",
  "landing.slot.one": "Two new activities",
  "landing.slot.one.meta": "The only genuinely new things today",
  "landing.slot.two": "Four revisited",
  "landing.slot.two.meta": "Met again days later, before they fade",
  "landing.slot.three": "Nothing to prepare",
  "landing.slot.three.meta": "Each one tells you what to do and what to say",
  "landing.slot.four": "One line of evidence",
  "landing.slot.four.meta": "A photo, a voice note, or what you saw",
  "landing.dayFoot": "Do them in any order, at any time. A missed day moves the plan rather than breaking it.",
  "landing.insideTitle": "What is inside",
  "landing.feature.day": "A day already planned",
  "landing.feature.day.body":
    "Two new activities and four revisited, chosen for you. Nothing is met once and then forgotten.",
  "landing.feature.words": "Words you can actually say",
  "landing.feature.words.body":
    "Every activity carries what to do, what you might say, and what to notice \u2014 in English or Urdu.",
  "landing.feature.evidence": "Evidence, not ticking boxes",
  "landing.feature.evidence.body":
    "Mastery moves because of something the child did, recorded as a photo, a voice note, or a line from you.",
  "landing.feature.mastery": "A mastery map",
  "landing.feature.mastery.body":
    "See at a glance which competencies are emerging, secure or transferring \u2014 for each child separately.",
  "landing.feature.portfolio": "A portfolio that builds itself",
  "landing.feature.portfolio.body":
    "Every piece of evidence is kept and dated, ready for the day somebody asks what your child has been doing.",
  "landing.feature.levels": "Playgroup to Class 5",
  "landing.feature.levels.body":
    "Three terms a year with stated goals, so you always know what this month is for.",
  "landing.stepsTitle": "How to start",
  "landing.step.add": "Add your child",
  "landing.step.add.body": "A name, an age and a level. The first day is built straight away \u2014 there is nothing to plan.",
  "landing.step.do": "Do the day",
  "landing.step.do.body": "Open Today and work through the moments in whatever order suits the morning.",
  "landing.step.record": "Record what happened",
  "landing.step.record.body": "One photo or one line per activity. The mastery map and the portfolio update themselves.",
  "landing.privacy":
    "Your family record is private. It is visible to you and to the Al Noor staff who support your family \u2014 never to other families, and never to search engines.",
  "landing.linkOverview": "Homeschooling with Al Noor",
  "landing.linkBenefits": "Benefits",
  "landing.linkGuidelines": "Guidelines",
  "landing.linkPolicy": "Policy",

  // Errors
  "error.title": "Something went wrong",
  "error.retry": "Try again",
  "error.load": "Could not load your family's learning.",
  "error.plan": "Could not open today's plan.",
  "error.save": "That change did not save.",
  "error.generic": "That did not save. Please try again.",

  // Home
  "home.greetingLine": "{greeting}, {name}.",
  "home.morning": "Good morning",
  "home.afternoon": "Good afternoon",
  "home.evening": "Good evening",
  "home.intro": "Today is light and practical — {count} small moments that turn ordinary family life into meaningful learning.",
  "home.introEmpty": "Add a few activities to the library and this page will build the day for you.",
  "home.begin": "Begin today",
  "home.print": "Print plan",
  "home.rhythm": "Today's rhythm",
  "home.ofComplete": "{done} of {total}",
  "home.momentsComplete": "moments complete",
  "home.streakCount": "{count} day",
  "home.streakLabel": "gentle streak",
  "home.planTitle": "Your family's plan",
  "home.todayWith": "Today with {name}",
  "home.about": "About {minutes} minutes, spread naturally across the day.",
  "home.nothingScheduled": "Nothing scheduled yet.",
  "home.emptyTitle": "Today's plan is empty",
  "home.emptyCopy":
    "A day is built automatically from the activity library. Once an admin has published activities for this stage, they appear here.",
  "home.growing": "Growing with confidence",
  "home.growingCopy": "{name}'s strongest movement right now is in {domain}.",
  "home.viewMap": "View map",
  "home.parentPractice": "Parent practice",
  "home.yourPractice": "Your small practice today",
  "home.defaultPractice": "After asking a question, quietly count to five before stepping in.",
  "home.waitWarmth": "Wait with warmth",
  "home.waitWarmthCopy": "Space helps {name} find their own words",

  // Today
  "today.eyebrow": "Today's gentle plan",
  "today.copy": "Do these in any order. The goal is connection and progress — not a perfect timetable.",
  "today.morning": "Morning",
  "today.afternoon": "Afternoon",
  "today.anytime": "Anytime",
  "today.viewGuide": "View guide",
  "today.markComplete": "Mark {title} complete",
  "today.markNotDone": "Mark {title} as not done",
  "today.emptyTitle": "No plan for today yet",
  "today.emptyCopy":
    "Once there are activities in the library, a day is built for this learner automatically the first time you open it.",
  "today.minutes": "{count} min",
  "today.child": "Child",
  "today.parent": "Parent",
  "today.openActivity": "Open activity",
  "today.doneToday": "Done today",
  "today.evidenceFor": "Record evidence for {title}",
  "catchup.titleYesterday": "Did {name} do yesterday's activities?",
  "catchup.titleDay": "Did {name} do the activities from {day}?",
  "catchup.copy": "Tick what you did. It moves {name}'s year forward, even on a day you worked from the printed plan.",
  "catchup.allDone": "Recorded. {name}'s year has moved on.",
  "catchup.dismiss": "Not this time",
  "evidence.preparing": "Preparing photo…",
  "portfolio.reviewPending": "Waiting for review",
  "portfolio.reviewApproved": "Reviewed",
  "portfolio.reviewChanges": "Something to try",
  "portfolio.feedbackFrom": "Al Noor's feedback:",
  "portfolio.newFeedback": "New",
  "home.feedbackTitle": "New feedback on {name}'s work",
  "home.feedbackCopy": "A teacher at Al Noor has looked at what you recorded and left a note.",
  "home.feedbackOpen": "Read it",
  "nav.newFeedback": "New feedback",
  "today.extrasTitle": "If there is time",
  "guide.narrationPlay": "Listen — 30 seconds",
  "join.tabJoin": "Start free",
  "trial.running": "You are in your free week.",
  "trial.daysLeft": "{count} days left of your free week.",
  "trial.lastDay": "Last day of your free week.",
  "trial.keepGoing": "Keep going",
  "paywall.title": "Your free week has ended",
  "paywall.endingTitle": "Keep the days coming",
  "paywall.yourChild": "your child",
  "paywall.lede":
    "Everything {name} has done stays here — every photo, the mastery map, the whole record. What stops is tomorrow’s plan.",
  "paywall.introOpen": "Introductory price — {remaining} of the first {limit} places left.",
  "paywall.bestValue": "Two months free",
  "paywall.perMonth": "a month",
  "paywall.perYear": "a year",
  "paywall.choose": "Choose this",
  "paywall.howToPay": "How to pay, for now",
  "paywall.step1": "Transfer the amount to",
  "paywall.step2": "Send us the receipt on WhatsApp with the email you signed up with.",
  "paywall.step3": "We switch your account back on, usually the same day.",
  "paywall.copy": "Copy IBAN",
  "paywall.copied": "Copied",
  "paywall.qrAlt": "Raast QR code to pay Al Noor Tuition Services",
  "paywall.qrCaption": "Or scan this Raast QR with any banking or wallet app.",
  "paywall.qrSave": "Save the QR",
  "paywall.otherAccounts": "Or pay into",
  "paywall.openQr": "Open the QR",
  "paywall.whatsappUs": "WhatsApp {number}",
  "paywall.whatsappGeneric": "Assalamu alaikum — I would like to continue with Al Noor Homeschool.",
  "paywall.whatsappMessage": "Assalamu alaikum — I would like the {plan} plan ({price}) for Al Noor Homeschool.",
  "paywall.manualNote":
    "Card and online payment are coming. Until then it is a transfer and a message, and we activate it by hand.",
  "paywall.recordKept": "Nothing is deleted. Your record stays exactly as it is.",
  "join.checkEmailTitle": "Check your email",
  "join.checkEmailCopy":
    "We have sent a link to {email}. Open it to confirm the address, and your free week starts straight away.",
  "join.tabSignIn": "Sign in",
  "join.freeWeek": "Your first seven days are free. No card, nothing to cancel.",
  "join.name": "Your name",
  "join.namePlaceholder": "The name your child’s work is kept under",
  "join.email": "Email",
  "join.password": "Password",
  "join.working": "One moment",
  "join.connectionError": "Could not reach Al Noor. Check your connection and try again.",
  "join.start": "Start the free week",
  "join.afterTrial": "Afterwards {monthly} a month, or {annual} for a year.",
  "join.introOpen": "Introductory price — {remaining} of the first {limit} places left.",
  "join.introWas": "Normally {monthly} a month or {annual} a year.",
  "join.monthsFree": "{n} months free",
  // Sign-in card
  "auth.google": "Continue with Google",
  "auth.googleOpening": "Opening Google…",
  "auth.or": "or with email",
  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "auth.passwordHint": "At least 8 characters.",
  "auth.strength.1": "Keep going — at least 8 characters.",
  "auth.strength.2": "Good. A number or symbol makes it stronger.",
  "auth.strength.3": "Strong password.",
  "auth.forgot": "Forgot password?",
  "auth.resetTitle": "Reset your password",
  "auth.resetCopy": "Enter the email you joined with and we will send you a link to choose a new password.",
  "auth.resetSend": "Send reset link",
  "auth.resetSent":
    "If {email} has an account, a reset link is on its way. Open it on this device and you will be asked for a new password.",
  "auth.backToSignIn": "Back to sign in",
  "auth.didYouMean": "Did you mean {email}?",
  "auth.exists": "You already have an account with this email. Sign in below.",
  "auth.entering": "Setting up your family space…",
  "auth.enteringHint": "Just a moment.",
  "auth.callback.oauth": "Google sign-in did not finish. Try again, or use your email below.",
  "auth.callback.pending": "This account is on hold. Please contact Al Noor and we will sort it out.",
  "auth.problem.exists": "You already have an account with this email. Sign in instead.",
  "auth.problem.wrongPassword": "That email and password do not match. Check them, or reset your password.",
  "auth.problem.rateLimited": "Too many attempts just now. Please wait a few minutes and try again.",
  "auth.problem.weakPassword": "Please choose a stronger password: at least 8 characters, and not a common one.",
  "auth.problem.badEmail": "That email address does not look right. Please check it.",
  "auth.problem.notConfirmed": "This account has not been activated yet. Contact Al Noor and we will switch it on.",
  "auth.problem.unknown": "Something went wrong. Please try again.",
  "auth.newPasswordTitle": "Choose a new password",
  "auth.newPasswordCopy":
    "You came in through a password reset link. Set a new password now, so you can sign in with it next time.",
  "auth.notNow": "Not now",
  "auth.savePassword": "Save password",
  "auth.passwordSaved": "New password saved",
  // First-run setup
  "onboard.progress": "Setup progress",
  "onboard.progress.curriculum": "Curriculum",
  "onboard.progress.children": "Your children",
  "onboard.hello": "Welcome, {name}",
  "onboard.helloPlain": "Welcome",
  "onboard.curriculum.title": "Which curriculum would you like to follow?",
  "onboard.curriculum.lede":
    "Every one comes with a complete daily plan — six short activities a day, each with a guide. Choose the emphasis that suits your family.",
  "onboard.curriculum.note": "You can change it later, for each child, from the family page.",
  "onboard.curriculum.pick": "Choose one to continue",
  "onboard.recommended": "Most families start here",
  "onboard.continue": "Continue",
  "onboard.children.title": "Tell us about your child",
  "onboard.children.titleMany": "Tell us about your children",
  "onboard.children.lede":
    "A name and an age is all we need. We will suggest the right class and have their first day ready.",
  "onboard.childName": "Child's name",
  "onboard.childN": "Child {n}",
  "onboard.childNamePlaceholder": "First name, e.g. Ayesha",
  "onboard.removeChild": "Remove {name}",
  "onboard.age": "Age",
  "onboard.birthday": "Birthday",
  "onboard.useBirthday": "Enter birthday instead",
  "onboard.useAge": "Choose an age instead",
  "onboard.needAge": "Choose their age so we can place them.",
  "onboard.class": "Starting class",
  "onboard.classPick": "Choose a class",
  "onboard.classHint": "Suggested from their age — change it if another level fits better.",
  "onboard.gender": "Girl or boy",
  "onboard.optional": "optional",
  "onboard.colour.blue": "Blue",
  "onboard.colour.mint": "Mint",
  "onboard.colour.rose": "Rose",
  "onboard.colour.gold": "Gold",
  "onboard.colour.lavender": "Lavender",
  "onboard.colour.green": "Green",
  "onboard.children.add": "Add another child",
  "onboard.saveProblem": "We could not save everyone just now.",
  "onboard.back": "Back",
  "onboard.createOne": "Build {name}'s first day",
  "onboard.createMany": "Build {count} learning plans",
  "onboard.building.one": "Building {name}'s first day…",
  "onboard.building.many": "Building your children's plans…",
  "onboard.building.copy": "Choosing today's activities and setting up their portfolio.",
  "onboard.ready.title": "All set!",
  "onboard.ready.copy": "Let's take a quick look around.",
  // The walk round the app
  "tour.yourChild": "your child",
  "tour.welcome.title": "Welcome, {name}!",
  "tour.welcome.titlePlain": "Welcome to Al Noor Homeschool!",
  "tour.welcome.body":
    "Here is a 30-second tour of where everything lives. You can skip it now and replay it any time from the menu.",
  "tour.learner.title": "{child}'s space",
  "tour.learner.body": "Everything you see is for {child}. Switch between children, or add another, from here.",
  "tour.home.body": "Your day at a glance: six short activities, ready to go. Tick each one off as you finish it.",
  "tour.today.body":
    "The full day, in order. Open any activity for a step-by-step guide — what to prepare, what to say, and how to tell it worked.",
  "tour.record.body":
    "A photo, a short video or a note of {child}'s work. Each one fills their portfolio and moves their mastery map.",
  "tour.curriculum.body": "The whole year, term by term. Need help with a subject? Ask for an Al Noor tutor right from here.",
  "tour.mastery.body": "See how {child} is growing, skill by skill — every step backed by real work you recorded.",
  "tour.portfolio.body": "Every piece of work in one place, with Al Noor's feedback on what you share.",
  "tour.language.title": "اردو / English",
  "tour.language.body": "Switch the whole app — activity guides included — to Urdu whenever you like.",
  "tour.done.title": "You're ready",
  "tour.done.body":
    "Start with today's first activity. The guide walks you through every step, and {child} will love having your attention.",
  "tour.count": "{n} of {total}",
  "tour.skip": "Skip tour",
  "tour.back": "Back",
  "tour.start": "Show me around",
  "tour.next": "Next",
  "tour.finish": "Let's begin",
  "tour.replay": "Take the tour",
  "guide.narrationStop": "Stop",
  "settings.narrationTitle": "Read the activity to me",
  "settings.narrationCopy":
    "A thirty-second summary, spoken to you — not to your child — when you open an activity. It says what the activity is and what your part in it is. Off by default.",
  "settings.narrationOn": "Play it automatically",
  "today.extrasCopy":
    "Two extras, offered not set. Music, movement, money and how machines think. Skipping them costs nothing — the day is already complete without them.",

  // Curriculum overview
  "curriculum.eyebrow": "The year ahead",
  "curriculum.title": "{name}'s {level} journey",
  "curriculum.copy": "See where today's small activities lead, term by term, without turning home learning into a race.",
  "curriculum.level": "Current level",
  "curriculum.yearAtGlance": "The whole year at a glance",
  "curriculum.honestProgress": "Progress counts days actually worked. Breaks do not spend the year or put a child behind.",
  "curriculum.daysWorked": "Days worked",
  "curriculum.terms": "Terms",
  "curriculum.activities": "Activities ready",
  "curriculum.termOverview": "Term overview",
  "curriculum.termNumber": "Term {count} of 3",
  "curriculum.current": "Now",
  "curriculum.complete": "Complete",
  "curriculum.upcoming": "Ahead",
  "curriculum.dayRange": "Days {start}–{end}",
  "curriculum.activityCount": "{count} activities",
  "curriculum.goalsPending": "The promises for this term are still being prepared.",
  "curriculum.extrasEyebrow": "Beside the day",
  "curriculum.extrasTitle": "Optional extras at this level",
  "curriculum.extrasCopy": "Offered beside the six, never instead of them. Chosen for breadth, so a child meets music one morning and money the next.",
  "curriculum.requestTutor": "Request a tutor",
  "curriculum.tutorRequested": "Tutor requested",
  "curriculum.requestTitle": "A tutor for {subject}",
  "curriculum.requestCopy": "You keep teaching everything else. We will find a verified tutor for this subject and confirm their rate and availability before anything starts.",
  "curriculum.requestDelivery": "How should they teach it?",
  "curriculum.requestSend": "Send the request",
  "curriculum.requestSent": "Request sent. We will be in touch about {subject}.",
  "curriculum.requestAlready": "You have already asked for a tutor for {subject}.",
  "curriculum.pathwayEyebrow": "Your pathway",
  "curriculum.pathwayCopy": "{total} weekly blocks, split like this.",
  "curriculum.blocksPerWeek": "{count} blocks a week",
  "curriculum.activitiesReady": "{count} activities ready at this level",
  "curriculum.noActivitiesYet": "No activities at this level yet",
  "curriculum.extrasOnly": "{count} as optional extras, not core lessons yet",
  "curriculum.teachersEyebrow": "Teaching",
  "curriculum.leadTeacherTitle": "Your lead teacher",
  "curriculum.tutorsTitle": "Your subject tutors",
  "curriculum.teachersPending":
    "Al Noor is arranging this. We will confirm availability, the timetable and the final monthly fee before teaching starts.",
  "home.awaitingTeacherTitle": "Your teacher is being arranged",
  "home.awaitingTeacherCopy":
    "Days are planned by your teacher on this route, not built automatically. Once Al Noor has confirmed availability and the timetable, the week appears here. The full activity library is on the Curriculum page meanwhile.",
  "curriculum.coveredAreas": "Areas covered",
  "curriculum.rhythmEyebrow": "How the plan works",
  "curriculum.rhythmTitle": "New learning, then enough return",
  "curriculum.rhythmCopy": "Each day introduces a little and revisits more from the recent window. Once a term is introduced, every activity keeps rotating evenly for consolidation.",
  "curriculum.sixDaily": "short activities in the day",
  "curriculum.twoNew": "genuinely new activities",
  "curriculum.fourRevisit": "spaced revisits from recent learning",
  "curriculum.emptyTitle": "Choose a level first",
  "curriculum.emptyCopy": "Set this learner's level in their profile to see the year ahead.",

  // Activity guide
  "guide.whatToDo": "What to do",
  "guide.whatToDoCopy":
    "Find a relaxed moment and invite {name} into the activity. Keep it conversational and stop while it still feels enjoyable.",
  "guide.whatToSay": "What you might say",
  "guide.whatToNotice": "What to notice",
  "guide.evidence": "Evidence",
  "guide.evidenceCopy": "{hint}. Capture only what helps you remember the learning.",
  "guide.inEnglish": "Say this part in English",
  "guide.inUrdu": "Say this part in Urdu",
  "guide.taughtIn": "Taught in {language}",
  "guide.childOutcome": "Child outcome",
  "guide.parentPractice": "Parent practice",
  "guide.markComplete": "Mark moment complete",
  "guide.markNotDone": "Mark as not done",
  "guide.updated": "Today's progress has been updated",

  // Evidence
  "evidence.eyebrow": "A small window into learning",
  "evidence.title": "Record evidence",
  "evidence.copy": "Keep it light. One useful observation is enough.",
  "evidence.photo": "Photo",
  "evidence.photoHint": "Capture a real moment",
  "evidence.voice": "Voice",
  "evidence.voiceHint": "Record an explanation",
  "evidence.writing": "Writing",
  "evidence.writingHint": "Photograph their work",
  "evidence.observation": "Observation",
  "evidence.observationHint": "Write what you noticed",
  "evidence.coach_note": "Coach note",
  "evidence.workPhoto": "Photo of their work",
  "evidence.voiceNote": "Voice note",
  "evidence.nothingRecorded": "Nothing recorded yet",
  "evidence.recording": "Recording…",
  "evidence.record": "Record",
  "evidence.stop": "Stop",
  "evidence.micProblem": "The microphone is not available. You can upload an audio file instead.",
  "evidence.titleLabel": "Title",
  "evidence.titlePlaceholder": "What was this moment?",
  "evidence.noticed": "What did you notice?",
  "evidence.noticedPlaceholder": "{name} explained their thinking without being asked to…",
  "evidence.showsProgress": "This shows progress in",
  "evidence.atWhatLevel": "At what level?",
  "evidence.linkedActivity": "Linked activity",
  "evidence.independent": "Independent family evidence",
  "evidence.shareCoach": "Share this with {name}'s assigned coach",
  "evidence.needTitle": "Give this a short title so it is findable later.",
  "evidence.cancel": "Cancel",
  "evidence.save": "Save to portfolio",
  "evidence.saving": "Saving…",
  "evidence.saved": "Evidence saved to {name}'s portfolio",
  "evidence.removed": "Removed from the portfolio",

  // Mastery
  "mastery.eyebrow": "Evidence over checkboxes",
  "mastery.title": "{name}'s mastery map",
  "mastery.copy":
    "A living picture of what they can understand, do independently, and transfer into real life.",
  "mastery.emptyTitle": "No mastery to show yet",
  "mastery.emptyCopy":
    "Mastery is built from evidence. Record a photo, a voice note or an observation against today's activities and the first meters will appear here.",
  "mastery.observed": "competencies observed",
  "mastery.secureCount": "secure or transfer",
  "mastery.developingCount": "currently developing",
  "mastery.supportCount": "need gentle support",
  "mastery.allAreas": "All areas",
  "mastery.piece": "{count} piece of evidence",
  "mastery.pieces": "{count} pieces of evidence",
  "mastery.view": "View",
  "mastery.nextStep": "Next meaningful step",
  "level.emerging": "Emerging",
  "level.developing": "Developing",
  "level.secure": "Secure",
  "level.transfer": "Transfer",

  // Portfolio
  "portfolio.eyebrow": "A story of real learning",
  "portfolio.title": "{name}'s portfolio",
  "portfolio.copy": "Photos, voice, writing and observations connected to meaningful competencies.",
  "portfolio.add": "Add evidence",
  "portfolio.filtered": "Filtered to one competency",
  "portfolio.showAll": "Show all",
  "portfolio.search": "Search evidence",
  "portfolio.allEvidence": "All evidence",
  "portfolio.emptyTitle": "Nothing captured yet",
  "portfolio.emptyCopy":
    "Open any of today's activities and tap the camera to record what you noticed. One useful observation is enough.",
  "portfolio.noMatchTitle": "Nothing matches that",
  "portfolio.noMatchCopy": "Try a different search or filter.",
  "portfolio.recordFirst": "Record the first one",
  "portfolio.linkedTo": "Linked to {count} competency",
  "portfolio.linkedToPlural": "Linked to {count} competencies",
  "portfolio.notLinked": "Not linked",
  "portfolio.delete": "Delete {title}",

  // Reports
  "reports.eyebrow": "Clear, useful reflection",
  "reports.title": "Learning reports",
  "reports.copy":
    "Understand what is secure, what is growing, the evidence behind it, and what to do next.",
  "reports.emptyTitle": "Not enough evidence for a report",
  "reports.emptyCopy":
    "A report is assembled from the evidence you record. Capture a few moments this week and this page will fill itself in.",
  "reports.print": "Print report",
  "reports.last30": "Last 30 days",
  "reports.soFar": "{name}'s learning so far",
  "reports.strongest":
    "Strongest in {domain}, with {count} competencies now secure or transferring into real life.",
  "reports.early":
    "Evidence is starting to build. Keep recording small moments and the picture sharpens quickly.",
  "reports.items": "{count} evidence items",
  "reports.observed": "{count} competencies observed",
  "reports.secure": "{count} secure or transfer",
  "reports.healthy": "Healthy progress",
  "reports.steady": "Steady progress",
  "reports.earlyDays": "Early days",
  "reports.becomingSecure": "What is becoming secure",
  "reports.becomingSecureNone": "Nothing is secure yet — that is completely normal early on.",
  "reports.growingNow": "What is growing now",
  "reports.growingNowNone": "Record a little more evidence to see what is on the move.",
  "reports.supportHelp": "Where support will help",
  "reports.supportHelpNone": "Nothing is flagged for extra support.",
  "reports.nextSteps": "The next gentle steps",
  "reports.nextStepsNone": "Next steps appear as competencies are observed.",

  // Learner form
  "learner.welcome": "Welcome to Al Noor Homeschool",
  "learner.newLearner": "A new learner",
  "learner.whoTitle": "Who are we learning with?",
  "learner.addTitle": "Add a learner",
  "learner.whoCopy":
    "Tell us about your child and we will build their first day of learning straight away.",
  "learner.addCopy": "Each learner gets their own plan, portfolio and mastery map.",
  "learner.name": "Their name",
  "learner.stage": "Stage",
  "learner.level": "Level",
  "learner.birthday": "Date of birth (optional)",
  "learner.colour": "Their colour",
  "learner.needName": "What should we call them?",
  "learner.start": "Start learning",
  "learner.ready": "Their first day is ready",
  "learner.noneTitle": "No learner selected",
  "learner.noneCopy": "Add a learner to see a daily plan, a portfolio and a mastery map.",
  "stage.foundation": "Foundation years",
  "stage.foundationHint": "Preschool, roughly ages 3–6",
  "stage.primary": "Primary journey",
  "stage.primaryHint": "Roughly ages 7–10",
  "stage.lower-secondary": "Lower secondary",
  "stage.lower-secondaryHint": "Roughly ages 11–14",

  // Common
  "resources.title": "Worksheets and resources",
  "resources.open": "Open",
  "resources.download": "Download",
  "resources.none": "No resources attached yet.",
  "resources.add": "Add a resource",
  "resources.label": "Title",
  "resources.url": "Link",
  "resources.kind": "Type",
  "resources.kind.worksheet": "Worksheet",
  "resources.kind.video": "Video",
  "resources.kind.link": "Link",
  "resources.kind.reading": "Reading",
  "resources.note": "Note for the parent",
  "resources.remove": "Remove",
  "resources.manage": "Resources",
  "common.close": "Close",
  "common.cancel": "Cancel",

  // -- Second pass: family record, sessions, suggestions, richer resources --
  "nav.family": "My family",
  "nav.suggestions": "Suggestions",
  "nav.learners": "Learners",
  "nav.matching": "Matching",

  // The enrolment choice. Every string here is on the one screen where a family
  // commits money, so none of it may fall back to English.
    "learner.levelHint": "Choose the learning level that fits now. You can change it later; age alone does not decide readiness.",
  "learner.choiceReview": "Your choice sets the starting plan. Review the term goals and adjust support after you begin.",
  "enrol.intro": "First choose who teaches. Then choose a curriculum emphasis. The weekly subject mix can be adjusted later.",
  "enrol.earlyYears": "For Playgroup, Nursery and KG, all choices use the same core daily activities. Your focus records a preference for parent and teacher planning; it is not a separate timetable or exam board. Begin with talk, play, movement and daily routines.",
  "curriculum.approachTitle": "How learning is designed",
  "curriculum.approachCopy": "Each term states what your child should be able to show. Try short guided activities, revisit ideas in daily life, and record what you notice. Adapt pace and language to the child.",
  "curriculum.standardNote": "The plan draws on Pakistan's early childhood curriculum and international early years principles. A pathway describes an emphasis; it does not confer Cambridge, Oxford or government accreditation.",
      "level.playgroup.title": "Playgroup",
  "level.playgroup.ages": "Ages 3–4",
  "level.nursery.title": "Nursery",
  "level.nursery.ages": "Ages 4–5",
  "level.kg.title": "Kindergarten",
  "level.kg.ages": "Ages 5–6",
  "level.class-1.title": "Class 1",
  "level.class-1.ages": "Ages 6–7",
  "level.class-2.title": "Class 2",
  "level.class-2.ages": "Ages 7–8",
  "level.class-3.title": "Class 3",
  "level.class-3.ages": "Ages 8–9",
  "level.class-4.title": "Class 4",
  "level.class-4.ages": "Ages 9–10",
  "level.class-5.title": "Class 5",
  "level.class-5.ages": "Ages 10–11",
  "subject.English": "English",
  "subject.Urdu": "Urdu",
  "subject.Mathematics": "Mathematics",
  "subject.Science": "Science",
  "subject.Islamiyat": "Islamiyat",
  "subject.Social Studies": "Social Studies",
  "subject.Global Perspectives": "Global Perspectives",
  "subject.Digital Literacy": "Digital Literacy",
  "subject.Quran and Arabic": "Quran and Arabic",
  "subject.Islamiyat and Character": "Islamiyat and Character",
  "subject.STEAM": "STEAM",
  "subject.Global and Financial Literacy": "Global and Financial Literacy",
  "subject.Character": "Character",
  "playgroup.levelTitle": "Playgroup",
  "class-1.term.1.summary": "Strengthen sound and sentence reading, meaningful writing, number sense and supported daily responsibility.",
  "class-1.term.1.goal.1": "Reads a short accessible English sentence using sound clues and says or shows its meaning.",
  "class-1.term.1.goal.2": "Recognises and reads familiar short Urdu words in context, asking for help with new letter forms.",
  "class-1.term.1.goal.3": "Writes or dictates a meaningful sentence, noticing spaces and an ending mark with a model.",
  "class-1.term.1.goal.4": "Builds and compares two-digit numbers with tens and ones, extending toward 100 as ready.",
  "class-1.term.1.goal.5": "Uses objects, drawings or counting to add and take away within 20 and explains one strategy.",
  "class-1.term.2.summary": "Use reading, writing and number to explain a choice, check a pattern and investigate an everyday question.",
  "class-1.term.2.goal.1": "Reads a short text or instruction and uses a detail to predict or answer a question.",
  "class-1.term.2.goal.2": "Writes or dictates two or three linked sentences on one topic and revises one part with support.",
  "class-1.term.2.goal.3": "Uses known doubles, grouping or counting to solve small additions and halvings; checks the result with objects.",
  "class-1.term.2.goal.4": "Measures a familiar object from zero on a ruler and compares two measurements.",
  "class-1.term.2.goal.5": "Asks a question, makes a prediction and tests a change while describing what stayed the same.",
  "class-1.term.3.summary": "Bring together reading, writing, practical mathematics and inquiry in short projects with help available.",
  "class-1.term.3.goal.1": "Reads or shares a suitable short book and retells key events in order with a text or picture clue.",
  "class-1.term.3.goal.2": "Writes or dictates an ordered account of a real event, then rereads and improves a part.",
  "class-1.term.3.goal.3": "Chooses objects, a drawing or a number strategy to solve a small story problem and checks the answer.",
  "class-1.term.3.goal.4": "Uses time, measurement, grouping or large money-value cards for a practical home question.",
  "class-1.term.3.goal.5": "Plans and completes a safe shared task with a checklist, asks for help and reflects on what worked.",
  "kg.term.1.summary": "Build sound, print and number sense through meaningful play while strengthening talk, movement and daily routines.",
  "kg.term.1.goal.1": "Hears an initial sound or rhyme in a familiar spoken word and joins an adult in blending short words.",
  "kg.term.1.goal.2": "Recognises several useful English or Urdu letters and tries a label using sounds, marks or a model.",
  "kg.term.1.goal.3": "Counts a real group one object at a time, working toward twenty as ready, and explores one more, one less and ways to make five.",
  "kg.term.1.goal.4": "Explains a familiar event in sequence or follows two linked directions with cues when needed.",
  "kg.term.1.goal.5": "Tries a practical care task and describes a feeling or need respectfully, with support.",
  "kg.term.2.summary": "Connect words, sentences and numbers to practical questions; predict, test and explain a small observation.",
  "kg.term.2.goal.1": "Reads a short decodable or familiar instruction with an adult, using sound clues and context.",
  "kg.term.2.goal.2": "Writes a meaningful short sentence with a model, noticing word spaces and an ending mark.",
  "kg.term.2.goal.3": "Joins and separates small groups within ten using objects, including counting all or counting on.",
  "kg.term.2.goal.4": "Builds a nearby two-digit number with tens and ones when ready and shares a group into two equal parts.",
  "kg.term.2.goal.5": "Predicts and tests an everyday question, then describes what changed or stayed the same.",
  "kg.term.3.summary": "Apply reading, writing, number and inquiry in small home projects, with growing independence and help available.",
  "kg.term.3.goal.1": "Reads or retells a short accessible book and points to a detail that supports an answer.",
  "kg.term.3.goal.2": "Writes or dictates two or three connected ideas, then rereads and improves one part with help.",
  "kg.term.3.goal.3": "Builds two-digit numbers as tens and ones and solves a practical add or take-away story using materials.",
  "kg.term.3.goal.4": "Explores a familiar clock time, large money cards, equal groups or shapes for a real purpose.",
  "kg.term.3.goal.5": "Uses a short checklist, asks before using a device, and reflects on a shared task or disagreement.",
  "nursery.term.1.summary": "Explore familiar sounds, names, quantities and routines through play. Notice attempts, not a fixed speed of mastery.",
  "nursery.term.1.goal.1": "Responds to and plays with sounds in familiar English or Urdu words, including a beginning sound when ready.",
  "nursery.term.1.goal.2": "Counts a small group of real objects by touching each once, working toward ten with support.",
  "nursery.term.1.goal.3": "Recognises their name card and experiments with marks, shapes or letters using comfortable tools.",
  "nursery.term.1.goal.4": "Talks about a familiar place, person, feeling or event through words, gesture, drawing or a home language.",
  "nursery.term.1.goal.5": "Joins a short daily routine such as washing hands or tidying with prompts and growing confidence.",
  "nursery.term.2.summary": "Connect and explain ideas: compare groups, hear and join sounds, describe sequences, and share practical jobs with others.",
  "nursery.term.2.goal.1": "Compares two small groups using matching or counting and explains which has more in their own way.",
  "nursery.term.2.goal.2": "Hears and joins sounds or syllables in familiar short English or Urdu words with an adult's help.",
  "nursery.term.2.goal.3": "Copies or forms a meaningful letter or part of their name with a visible model when ready.",
  "nursery.term.2.goal.4": "Sorts safe everyday objects by one feature and says or shows why they belong together.",
  "nursery.term.2.goal.5": "Takes a turn, asks for help and follows two linked directions with support in play.",
  "nursery.term.3.summary": "Use growing skills in everyday situations: find useful print, count for a purpose, tell a story and carry out a small shared responsibility.",
  "nursery.term.3.goal.1": "Recognises their name in English or Urdu and attempts to write or make its letters using a model as needed.",
  "nursery.term.3.goal.2": "Notices or sounds out a familiar printed word in a story, sign or label with support.",
  "nursery.term.3.goal.3": "Counts real objects to solve a small practical question such as setting places or sharing blocks.",
  "nursery.term.3.goal.4": "Retells two or three parts of a familiar story or recent event in sequence using speech, gesture or pictures.",
  "nursery.term.3.goal.5": "Completes a short two-step job with an agreed cue or reminder and explains what they did.",
  "playgroup.term.1.summary": "Connect through names, turn taking and familiar routines; explore objects and movement at a comfortable pace.",
  "playgroup.term.1.goal.1": "Responds to a familiar name and communicates a need through speech, gesture or sign.",
  "playgroup.term.1.goal.2": "Shares a brief back and forth exchange with an adult, with time to respond.",
  "playgroup.term.1.goal.3": "Explores one or two objects by touching and moving each, without a required spoken count.",
  "playgroup.term.1.goal.4": "Joins a familiar handwashing, greeting or tidying routine with support.",
  "playgroup.term.1.goal.5": "Tries moving, grasping or making marks in a safe play space.",
  "playgroup.term.2.summary": "Use familiar words and gestures in play, notice small groups and practise everyday independence.",
  "playgroup.term.2.goal.1": "Follows a familiar one step request with a gesture or spoken cue.",
  "playgroup.term.2.goal.2": "Makes or notices a small group of up to four objects during play, with help when needed.",
  "playgroup.term.2.goal.3": "Matches familiar objects and tries a simple repeating pattern using real materials.",
  "playgroup.term.2.goal.4": "Practises carrying, pouring or threading with safe materials and adult support.",
  "playgroup.term.2.goal.5": "Requests a turn or help using words, gestures or a home language.",
  "playgroup.term.3.summary": "Share a small story, solve a hands on problem and join more of the daily routine.",
  "playgroup.term.3.goal.1": "Shows or tells what happened first and next in a familiar experience.",
  "playgroup.term.3.goal.2": "Touches or moves up to five objects while counting together, without pressure for perfect recall.",
  "playgroup.term.3.goal.3": "Tries another way when a simple construction or puzzle does not work.",
  "playgroup.term.3.goal.4": "Takes part in dressing, tidying or caring for a plant with growing independence.",
  "playgroup.term.3.goal.5": "Notices how another person feels and tries a kind response with adult modelling.",
  "curriculum.goalLabel": "Observable goals",
    "enrol.earlyFocus": "Play based focus",
  "enrol.earlyPathway.balanced.title": "Whole-child play",
  "enrol.earlyPathway.balanced.summary": "Language, movement, relationships, discovery and daily routines together.",
  "enrol.earlyPathway.pakistan.title": "Home language & community",
  "enrol.earlyPathway.pakistan.summary": "More Urdu and familiar local stories, objects and routines.",
  "enrol.earlyPathway.cambridge.title": "Language & discovery",
  "enrol.earlyPathway.cambridge.summary": "Bilingual talk, exploration and creative play informed by international early years principles.",
  "enrol.earlyPathway.faith.title": "Faith & character",
  "enrol.earlyPathway.faith.summary": "Kindness, gratitude, greetings and short family faith routines through play.",
  "enrol.earlyPathway.future_skills.title": "Curiosity & making",
  "enrol.earlyPathway.future_skills.summary": "Hands on building, noticing patterns and trying another way.",
  "curriculum.earlyYearsPlan": "There is no fixed 20 block subject timetable at this level. Follow the term goals through brief play, talk and everyday routines; slow down or revisit whenever needed.",
  "enrol.routeQuestion": "How should Al Noor support this child?",
  "enrol.route.parent_led.title": "Parent-Led Homeschool",
  "enrol.route.parent_led.summary": "You teach; Al Noor plans every day.",
  "enrol.route.parent_led.b1": "Six short daily activities",
  "enrol.route.parent_led.b2": "Evidence, portfolio and reports",
  "enrol.route.parent_led.b3": "English and Urdu",
  "enrol.route.parent_led.price": "7 days free, then the published monthly or annual app fee",
  "enrol.route.subject_support.title": "Subject Tutor Support",
  "enrol.route.subject_support.summary": "You lead the homeschool; verified tutors handle selected subjects.",
  "enrol.route.subject_support.b1": "Choose a complete pathway",
  "enrol.route.subject_support.b2": "Add only the subjects you need",
  "enrol.route.subject_support.b3": "Specialists quoted separately",
  "enrol.route.subject_support.price": "Existing subject-tuition rates apply",
  "enrol.route.teacher_led.title": "Teacher-Led Homeschool",
  "enrol.route.teacher_led.summary": "A lead teacher runs a structured five-day homeschool programme.",
  "enrol.route.teacher_led.b1": "2–3 hours daily, five days a week",
  "enrol.route.teacher_led.b2": "Timetable, attendance and reports",
  "enrol.route.teacher_led.b3": "Specialist teachers can join",
  "enrol.route.teacher_led.price": "Online PKR 5,000–25,000/child · Home PKR 20,000–60,000/month",

  "enrol.blocksQuestion": "Adjust the week",
  "enrol.blocksHelp": "Move blocks between subjects to suit this child. The week stays at {total}, so adding one takes one from the largest subject.",
  "enrol.blocksTotal": "{used} of {total} blocks allocated",
  "enrol.blocksReset": "Back to the pathway default",
  "enrol.addBlock": "Add a block to {subject}",
  "enrol.removeBlock": "Take a block from {subject}",
  "enrol.tutorLater": "You do not have to decide about tutors now. Once you start, every subject and every block on the curriculum page has a “Request a tutor” button — ask when you know what you actually need help with.",
  "enrol.pathwayQuestion": "Choose a curriculum pathway",
  "enrol.blocks": "20 weekly blocks",
  "enrol.pathway.balanced.title": "Balanced Primary",
  "enrol.pathway.balanced.summary": "A broad, dependable default for families who want us to decide.",
  "enrol.pathway.balanced.b1": "English and Urdu",
  "enrol.pathway.balanced.b2": "Mathematics and science",
  "enrol.pathway.balanced.b3": "Islamiyat and social studies",
  "enrol.pathway.balanced.price": "Recommended",
  "enrol.pathway.pakistan.title": "Pakistan Curriculum",
  "enrol.pathway.pakistan.summary": "Strong local-language and school-return alignment.",
  "enrol.pathway.pakistan.b1": "Urdu 4 · English 4",
  "enrol.pathway.pakistan.b2": "Maths 5 · Science 3",
  "enrol.pathway.pakistan.b3": "Islamiyat 2 · Social Studies 2",
  "enrol.pathway.pakistan.price": "Local pathway",
  "enrol.pathway.cambridge.title": "Cambridge Bilingual",
  "enrol.pathway.cambridge.summary": "International primary foundations with local-language continuity.",
  "enrol.pathway.cambridge.b1": "English 5 · Maths 5 · Science 4",
  "enrol.pathway.cambridge.b2": "Urdu 2",
  "enrol.pathway.cambridge.b3": "Global Perspectives 2 · Digital Literacy 2",
  "enrol.pathway.cambridge.price": "International pathway",
  "enrol.pathway.faith.title": "Faith-Integrated Primary",
  "enrol.pathway.faith.summary": "Core academics with additional Quran, Arabic and character learning.",
  "enrol.pathway.faith.b1": "English 4 · Maths 5",
  "enrol.pathway.faith.b2": "Urdu 3 · Science 3",
  "enrol.pathway.faith.b3": "Quran/Arabic 3 · Islamiyat/Character 2",
  "enrol.pathway.faith.price": "Faith-focused pathway",
  "enrol.pathway.future_skills.title": "Future Skills & STEAM",
  "enrol.pathway.future_skills.summary": "Project-led science, digital and financial capability.",
  "enrol.pathway.future_skills.b1": "English 4 · Maths 5 · STEAM 4",
  "enrol.pathway.future_skills.b2": "Urdu 2 · Digital Literacy 2",
  "enrol.pathway.future_skills.b3": "Global/Financial Literacy 2 · Character 1",
  "enrol.pathway.future_skills.price": "Project-focused pathway",

  "enrol.deliveryQuestion": "Teaching delivery",
  "enrol.delivery.online.label": "Online",
  "enrol.delivery.online.hint": "Available across Pakistan and abroad",
  "enrol.delivery.home.label": "Home visiting",
  "enrol.delivery.home.hint": "Subject to teacher and area availability",
  "enrol.delivery.hybrid.label": "Hybrid",
  "enrol.delivery.hybrid.hint": "Combine online and home sessions",

  "enrol.subjectsQuestion": "Which subjects need a tutor?",
  "enrol.subject.english": "English",
  "enrol.subject.urdu": "Urdu",
  "enrol.subject.mathematics": "Mathematics",
  "enrol.subject.science": "Science",
  "enrol.subject.islamiyat": "Islamiyat",
  "enrol.subject.quran": "Quran",
  "enrol.subject.socialStudies": "Social Studies",
  "enrol.subject.digitalLiteracy": "Digital Literacy",
  "enrol.subjectsNote": "Standard subject-tuition prices apply. Specialist support is quoted separately.",

  "enrol.priceNote.title": "Indicative monthly range",
  "enrol.priceNote.online": "Online: PKR 5,000–25,000 per child",
  "enrol.priceNote.home": "Home visiting: PKR 20,000–60,000 for 2–3 hours daily, five days a week",
  "enrol.priceNote.footnote":
    "Final fees depend on level, pathway, schedule, location, teacher and specialist requirements.",

  "home.requestSent":
    "Your {pathway} request has been sent to Al Noor. We will confirm teacher availability, the timetable and the final monthly fee before teaching starts.",
  "home.viewTeachers": "View teachers",

  "learner.needLevel": "Choose a learning level to build this child's plan.",
  "learner.needRoute": "Choose how Al Noor will support this child.",
  "learner.needPathway": "Choose a curriculum pathway.",
  "learner.needDelivery": "Choose online, home, or hybrid teaching.",
  "learner.needSubjects": "Choose at least one subject for tutor support.",
  "learner.blocksOverAllocated": "The week can only hold {total} blocks. Take some off a subject before adding more.",
  "learner.requestLocked":
    "This child’s details are saved. Their teaching request is already being arranged, so the route and pathway on it have not changed — please contact us to change those.",
  "nav.review": "Review",
  "common.save": "Save",
  "common.saved": "Saved",
  "common.edit": "Edit",
  "common.all": "All",

  "guide.helpTitle": "Resources and examples",
  "guide.helpCopy": "{count} attached to this activity.",
  "guide.helpWithExamples": "{count} to use, and {examples} showing what finished work looks like.",
  "guide.seeHelp": "See resources and examples",
  "guide.hideHelp": "Hide them",
  "mastery.viewSeen": "What we have seen",
  "mastery.viewGoals": "All goals ({done}/{total})",
  "mastery.goalGroupCount": "{done} of {total} started",
  "mastery.notStarted": "Not started yet",
  "guide.storyToggle": "How this usually goes",
  "guide.storyTitle": "A real moment, not a perfect one",
  "admin.previewFooter": "Preview — this is what a parent sees. Their buttons are hidden here.",
  "ai.helpTitle": "Need this to land differently?",
  "ai.simpler": "Make it simpler",
  "ai.harder": "Make it harder",
  "ai.bored": "They got bored",
  "ai.refused": "They will not join in",
  "ai.explain": "Explain it to me",
  "ai.thinking": "Thinking…",
  "ai.disclaimer": "Written by AI from this activity. You know your child — use what fits.",
  "ai.again": "Ask again",
  "ai.unavailable": "The helper is not switched on yet. The activity above still works as written.",

  "resources.examples": "What it looks like when it works",
  "resources.examplesCopy": "Not a template to copy — just so you know what you are aiming at.",
  "resources.showMore": "Read all of it",
  "resources.showLess": "Show less",
  "resources.loading": "Opening…",
  "resources.unavailable": "This file could not be opened. Please try again.",
  "resources.retry": "Retry files",
  "resources.print": "Print / save as PDF",
  "offline.save": "Save illustrated offline pack",
  "offline.preparing": "Preparing pack…",
  "offline.failed": "The pack could not be saved. Check your connection and try again.",
  "panel.failed": "This section could not load. Check your connection and reload.",
  "panel.reload": "Reload page",
  "resources.kind.pdf": "PDF",
  "resources.kind.image": "Picture",
  "resources.kind.audio": "Audio",
  "resources.kind.text": "Note",
  "resources.kind.document": "Document",

  "learner.profile": "Profile",
  "learner.editTitle": "{name}’s profile",
  "learner.editCopy": "The more we know about them, the better the programme can advise you.",
  "learner.profileSaved": "Profile saved",
  "learner.moreDetail": "Tell us more about them (optional)",
  "learner.aboutSection": "About them",
  "learner.planSection": "Their plan",
  "learner.gender": "Gender",
  "learner.gender.female": "Girl",
  "learner.gender.male": "Boy",
  "learner.gender.unspecified": "Prefer not to say",
  "learner.homeLanguage": "Language at home",
  "learner.city": "City",
  "learner.country": "Country",
  "learner.about": "What are they like?",
  "learner.aboutHint": "What they love, what they are good at, what makes them switch off.",
  "learner.support": "Anything we should know",
  "learner.supportHint": "A diagnosis, a health need, a subject that is hard. Only what helps us teach them well.",
  "learner.schooling": "Where they were before",
  "learner.schoolingHint": "A school, another homeschool, or nowhere yet — all are fine.",
  "learner.goals": "What are you aiming at this year?",
  "learner.goalsHint": "In your own words.",
  "learner.startedOn": "Started homeschooling",
  "learner.weeklyTarget": "Minutes of learning a week",
  "learner.weeklyTargetHint": "Your target, not ours. Your hours are shown against it, and nothing is withheld if you fall short.",
  "learner.enrolment": "Enrolment",
  "learner.enrolment.active": "Learning",
  "learner.enrolment.paused": "Paused",
  "learner.enrolment.completed": "Finished",

  "family.eyebrow": "Your family",
  "family.title": "Children and hours",
  "family.copy": "Their profiles, your details, and a record of the time they have actually spent learning.",
  "family.yourDetails": "Your details",
  "family.yourDetailsCopy": "How we reach you, and what you are looking for from Al Noor.",
  "family.phone": "Phone",
  "family.city": "City",
  "family.country": "Country",
  "family.approach": "How you like to homeschool",
  "family.approachHint": "Structured, relaxed, unschooling, a mix — or unsure yet.",
  "family.lookingFor": "What you want from Al Noor",
  "family.lookingForHint": "A full curriculum, help with one subject, Islamic studies, exam preparation…",
  "family.howHeard": "How you found us",
  "family.sharingDefault": "Share new evidence with our coach by default",
  "family.currentlyOpen": "Currently open",
  "family.switchTo": "Learn with {name}",
  "family.editProfile": "Edit profile",
  "family.archive": "Archive this child",
  "family.archiveConfirm": "Their whole record is kept — only the daily plan stops.",
  "family.archived": "Archived. Their portfolio is still here.",

  "sessions.eyebrow": "Time spent",
  "sessions.title": "{name}’s sessions",
  "sessions.copy": "Ticking an activity records its minutes for you. Add anything else you did here.",
  "sessions.add": "Add a session",
  "sessions.addTitle": "Record a session",
  "sessions.addCopy": "Something {name} did that the plan did not suggest — a museum, an hour of reading, a lesson with a tutor.",
  "sessions.what": "What did they do?",
  "sessions.whatHint": "Library visit, and reading afterwards",
  "sessions.minutes": "How many minutes?",
  "sessions.when": "When?",
  "sessions.note": "Anything worth remembering",
  "sessions.noteHint": "Chose three books on her own and read two of them to her brother.",
  "sessions.save": "Record it",
  "sessions.needTitle": "Say what they did.",
  "sessions.hours": "{hours} h",
  "sessions.hoursPerWeek": "{hours} h a week",
  "sessions.thisWeek": "this week",
  "sessions.ofTarget": "of your weekly target",
  "sessions.recorded": "recorded in total",
  "sessions.emptyTitle": "No hours recorded yet",
  "sessions.emptyCopy": "Tick an activity complete and its minutes appear here. Anything else, add it by hand.",
  "sessions.untitled": "Activity",
  "sessions.remove": "Remove this session",

  "suggest.eyebrow": "Tell us",
  "suggest.pageTitle": "Suggestions and questions",
  "suggest.pageCopy": "You see things we cannot. Send them here and we will answer.",
  "suggest.adminTitle": "What families have sent",
  "suggest.adminCopy": "Every suggestion gets an answer the family reads next to what they sent.",
  "suggest.add": "Send something",
  "suggest.addTitle": "Send us something",
  "suggest.addCopy": "An activity that would work, a resource worth sharing, something that did not work, or a question.",
  "suggest.kind": "What is it?",
  "suggest.kind.activity": "An activity",
  "suggest.kind.resource": "A resource",
  "suggest.kind.improvement": "Something to fix",
  "suggest.kind.question": "A question",
  "suggest.kind.idea": "An idea",
  "suggest.title": "In one line",
  "suggest.titleHint": "Counting with prayer beads works better than spoons",
  "suggest.body": "Tell us more",
  "suggest.bodyHint": "What you tried, what happened, and what you would suggest.",
  "suggest.aboutChild": "About which child?",
  "suggest.noChild": "Not about one child",
  "suggest.aboutActivity": "About which activity?",
  "suggest.noActivity": "Not about one activity",
  "suggest.link": "A link, if you have one",
  "suggest.file": "A file, if you have one",
  "suggest.send": "Send it",
  "suggest.needBoth": "A line, and a little more detail underneath it.",
  "suggest.status.pending": "Waiting",
  "suggest.status.accepted": "Accepted",
  "suggest.status.declined": "Not this time",
  "suggest.reply": "Al Noor replied",
  "suggest.replyLabel": "Your reply to the family",
  "suggest.replyHint": "What you will do with it, or why not.",
  "suggest.accept": "Accept",
  "suggest.decline": "Decline",
  "suggest.withdraw": "Withdraw it",
  "suggest.attachment": "Attachment",
  "suggest.emptyTitle": "Nothing sent yet",
  "suggest.emptyCopy": "If an activity did not work, or you found something worth sharing, this is where it goes.",
  "suggest.emptyAdminTitle": "Nothing here",
  "suggest.emptyAdminCopy": "No family has sent anything matching that filter.",
};

/**
 * English is bundled; every other language is fetched when it is chosen.
 *
 * English is not just one of the languages here — it is the fallback for any key
 * a translation has not reached, so `t` can never be left with nothing to
 * return. The others are ~45 kB each of strings that most visitors will never
 * read, which is worth keeping out of the first download.
 */
const CATALOGUES: Record<string, () => Promise<Dictionary>> = {
  ur: () => import("./locales/ur").then((module) => module.ur),
};

const loaded: Record<string, Dictionary> = { en };

function loadCatalogue(code: string): Promise<Dictionary> {
  if (loaded[code]) return Promise.resolve(loaded[code]);
  const fetchIt = CATALOGUES[code];
  if (!fetchIt) return Promise.resolve(en);
  return fetchIt()
    .then((dictionary) => (loaded[code] = dictionary))
    // A family offline mid-session should read an English app, not a blank one.
    .catch(() => en);
}

/**
 * The English dictionary, for the test that keeps the translations in step.
 *
 * A key present in English and missing in Urdu does not fail anywhere — the
 * lookup falls through and an Urdu-speaking family is shown the raw key, or
 * English, on whichever screen was forgotten. That is exactly how the enrolment
 * choice came to be four translated headings around entirely English content.
 */
export const __english: Dictionary = en;

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

type I18nValue = { locale: LocaleCode; setLocale: (next: LocaleCode) => void; t: Translate; dir: "ltr" | "rtl" };

const I18nContext = createContext<I18nValue | null>(null);

const known = (code: string) => locales.some((locale) => locale.code === code);

function readStoredLocale(): LocaleCode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && known(stored)) return stored;
  } catch {
    // Private browsing can refuse localStorage entirely; English is fine.
  }
  // Fall back to the browser's preference before English, so an Urdu-speaking
  // family does not have to find the picker on their first visit.
  const preferred = typeof navigator === "undefined" ? "" : navigator.language.slice(0, 2).toLowerCase();
  return known(preferred) ? preferred : DEFAULT_LOCALE;
}

/**
 * Start fetching the first language while the module is still evaluating, so it
 * is in flight before React has mounted and long before the session check comes
 * back. Nothing awaits this here; the provider picks it up.
 */
const firstLocale = typeof window === "undefined" ? DEFAULT_LOCALE : readStoredLocale();
void loadCatalogue(firstLocale);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<LocaleCode>(firstLocale);
  // The catalogue in use, held as state so that its arrival re-renders every
  // screen reading strings. Null only before the first one lands.
  const [dictionary, setDictionary] = useState<Dictionary | null>(() => loaded[firstLocale] ?? null);
  const dir = localeDir(locale);

  useEffect(() => {
    if (loaded[locale]) {
      setDictionary(loaded[locale]);
      return;
    }
    let live = true;
    // Until it arrives the previous language keeps rendering, so switching
    // language never flashes a half-empty screen.
    void loadCatalogue(locale).then((catalogue) => {
      if (live) setDictionary(catalogue);
    });
    return () => {
      live = false;
    };
  }, [locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  const setLocale = useCallback((next: LocaleCode) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not being able to remember the choice is survivable; ignoring it is not
      // worth failing the switch over.
    }
  }, []);

  const t = useCallback<Translate>(
    (key, vars) => {
      // English is the fallback for any key a translation has not reached yet,
      // so a partially translated release shows English words rather than raw
      // dotted keys.
      const template = dictionary?.[key] ?? en[key] ?? key;
      if (!vars) return template;
      return template.replace(/\{(\w+)\}/g, (match, name) =>
        name in vars ? String(vars[name]) : match,
      );
    },
    [dictionary],
  );

  const value = useMemo(() => ({ locale, setLocale, t, dir }), [locale, setLocale, t, dir]);
  // Hold the first paint until the chosen language is in hand. The page is
  // blank for that moment anyway, and it is the difference between an Urdu
  // family opening the app in Urdu and watching it flip out of English.
  if (!dictionary) return null;
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider.");
  return value;
}

/** Shorthand for the common case of only needing the translate function. */
export function useT() {
  return useI18n().t;
}
